import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTabData } from '../hooks/useTabData';
import { fetchScheduleAus } from '../services/apiService';
import { SkeletonLoader } from './SkeletonLoader';
import { getDisciplineName } from '../config/disciplineNames';
import { getCountryName } from '../config/countryNames';
import ausFlagIcon from '../assets/images/icons/national-flag-aus.svg';

const FLAG_ICON_MODULES = import.meta.glob('../assets/images/icons/national-flag-*.svg', {
  eager: true,
  import: 'default'
});

const DEFAULT_FLAG_MODULE = import.meta.glob('../assets/images/icons/icon-default.svg', {
  eager: true,
  import: 'default'
});

const FLAG_ICONS_BY_CODE = Object.entries(FLAG_ICON_MODULES).reduce((acc, [filePath, assetUrl]) => {
  const match = filePath.match(/national-flag-([a-z0-9-]+)\.svg$/);
  if (!match) return acc;
  acc[match[1].toUpperCase()] = assetUrl;
  return acc;
}, {});

const DEFAULT_FLAG_ICON = Object.values(DEFAULT_FLAG_MODULE)[0] || '';

export const AussiesSchedule = ({ isActive }) => {
  const { data, isLoading, error, refetch } = useTabData('aussies', fetchScheduleAus);
  const showLocation = false;
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [medalOnly, setMedalOnly] = useState(false);
  const eventsListRef = useRef(null);
  const [pendingScrollTarget, setPendingScrollTarget] = useState(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const getDayKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDayLabel = (date) => {
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getNearestDayKey = (dayOptions) => {
    if (!dayOptions.length) return '';

    const now = Date.now();
    return dayOptions.reduce((nearest, option) => {
      if (!nearest) return option;
      const nearestDiff = Math.abs(nearest.timestamp - now);
      const optionDiff = Math.abs(option.timestamp - now);
      return optionDiff < nearestDiff ? option : nearest;
    }, null).key;
  };

  const getDefaultDayKey = (dayOptions, todayDayKey) => {
    if (!dayOptions.length) return '';

    const hasToday = dayOptions.some((option) => option.key === todayDayKey);
    if (hasToday) return todayDayKey;

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const nextUpcoming = dayOptions.find((option) => option.timestamp >= todayStart.getTime());
    if (nextUpcoming) return nextUpcoming.key;

    return getNearestDayKey(dayOptions);
  };

  const hasValidDate = (dateStr) => {
    if (!dateStr) return false;
    return !Number.isNaN(new Date(dateStr).getTime());
  };

  const aussieEvents = useMemo(() => {
    if (!data) return [];

    return [...data]
      .filter((event) => {
        const orgs = event.Organisations || [];
        return orgs.some((org) => org.Code === 'AUS');
      })
      .filter((event) => hasValidDate(event.StartDate))
      .sort((a, b) => {
        return new Date(a.StartDate) - new Date(b.StartDate);
      });
  }, [data]);

  const dayOptions = useMemo(() => {
    const days = new Map();

    aussieEvents.forEach((event) => {
      const date = new Date(event.StartDate);
      const dayKey = getDayKey(date);

      if (!days.has(dayKey)) {
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        days.set(dayKey, {
          key: dayKey,
          label: formatDayLabel(date),
          timestamp: dayStart
        });
      }
    });

    return [...days.values()].sort((a, b) => a.timestamp - b.timestamp);
  }, [aussieEvents]);

  const todayDayKey = getDayKey(new Date());

  const nearestDayKey = useMemo(() => getNearestDayKey(dayOptions), [dayOptions]);

  const defaultDayKey = useMemo(() => getDefaultDayKey(dayOptions, todayDayKey), [dayOptions, todayDayKey]);

  const hasTodayEvents = useMemo(() => dayOptions.some((option) => option.key === todayDayKey), [dayOptions, todayDayKey]);

  const disciplineOptions = useMemo(() => {
    const disciplines = [...new Set(aussieEvents
      .map((event) => event.DisciplineCode)
      .filter(Boolean))];

    return disciplines.sort((a, b) => a.localeCompare(b));
  }, [aussieEvents]);

  const eventsByDay = useMemo(() => {
    return aussieEvents.reduce((acc, event) => {
      const dayKey = getDayKey(new Date(event.StartDate));
      if (!acc[dayKey]) acc[dayKey] = [];
      acc[dayKey].push(event);
      return acc;
    }, {});
  }, [aussieEvents]);

  const disciplineStats = useMemo(() => {
    return aussieEvents.reduce((acc, event) => {
      const code = event.DisciplineCode;
      if (!code) return acc;

      if (!acc[code]) {
        acc[code] = { total: 0, current: 0 };
      }

      acc[code].total += 1;

      const eventDayKey = getDayKey(new Date(event.StartDate));
      if (selectedDay === 'all' || eventDayKey === selectedDay) {
        acc[code].current += 1;
      }

      return acc;
    }, {});
  }, [aussieEvents, selectedDay]);

  const allDisciplineCount = useMemo(() => {
    if (selectedDay === 'all') return aussieEvents.length;
    return (eventsByDay[selectedDay] || []).length;
  }, [selectedDay, aussieEvents, eventsByDay]);

  useEffect(() => {
    if (!dayOptions.length) {
      setSelectedDay('');
      return;
    }

    setSelectedDay((prevDay) => {
      if (prevDay && dayOptions.some((option) => option.key === prevDay)) {
        return prevDay;
      }

      return defaultDayKey;
    });
  }, [dayOptions, defaultDayKey]);

  useEffect(() => {
    setSelectedDiscipline((prevDiscipline) => {
      if (prevDiscipline === 'all') return prevDiscipline;
      if (disciplineOptions.includes(prevDiscipline)) return prevDiscipline;
      return 'all';
    });
  }, [disciplineOptions]);

  const filteredEvents = useMemo(() => {
    if (!selectedDay) return [];
    let dayEvents = selectedDay === 'all' ? aussieEvents : (eventsByDay[selectedDay] || []);

    if (selectedDiscipline !== 'all') {
      dayEvents = dayEvents.filter((event) => event.DisciplineCode === selectedDiscipline);
    }

    if (medalOnly) {
      dayEvents = dayEvents.filter((event) => event.Medal);
    }

    return dayEvents;
  }, [selectedDay, aussieEvents, eventsByDay, selectedDiscipline, medalOnly]);

  const nextUpcomingEvent = useMemo(() => {
    const now = Date.now();
    let candidateEvents = [...aussieEvents];

    if (selectedDiscipline !== 'all') {
      candidateEvents = candidateEvents.filter((event) => event.DisciplineCode === selectedDiscipline);
    }

    if (medalOnly) {
      candidateEvents = candidateEvents.filter((event) => event.Medal);
    }

    return candidateEvents.find((event) => new Date(event.StartDate).getTime() >= now) || null;
  }, [aussieEvents, selectedDiscipline, medalOnly]);

  const hasNextUpcomingEvent = Boolean(nextUpcomingEvent);

  const medalEventCount = useMemo(() => {
    let baseEvents = selectedDay === 'all' ? aussieEvents : (eventsByDay[selectedDay] || []);

    if (selectedDiscipline !== 'all') {
      baseEvents = baseEvents.filter((event) => event.DisciplineCode === selectedDiscipline);
    }

    return baseEvents.filter((event) => event.Medal).length;
  }, [selectedDay, aussieEvents, eventsByDay, selectedDiscipline]);

  const selectedDaySummary = useMemo(() => {
    if (selectedDay === 'all') return 'All days';
    const selectedOption = dayOptions.find((option) => option.key === selectedDay);
    return selectedOption?.label || 'Day';
  }, [selectedDay, dayOptions]);

  const selectedDisciplineSummary = useMemo(() => {
    if (selectedDiscipline === 'all') return 'All disciplines';
    return getDisciplineName(selectedDiscipline);
  }, [selectedDiscipline]);

  const hasActiveFilters = useMemo(() => {
    const defaultDay = defaultDayKey || '';
    return selectedDiscipline !== 'all' || medalOnly || (!!selectedDay && selectedDay !== defaultDay);
  }, [selectedDay, selectedDiscipline, medalOnly, defaultDayKey]);

  const resetFilters = () => {
    setSelectedDay(defaultDayKey || 'all');
    setSelectedDiscipline('all');
    setMedalOnly(false);
    setPendingScrollTarget(null);
  };

  const scrollToEvent = (target) => {
    const listEl = eventsListRef.current;
    if (!listEl || !target) return false;

    const cards = listEl.querySelectorAll('.event-card[data-event-code][data-event-start]');
    const eventCard = Array.from(cards).find((card) => {
      return card.dataset.eventCode === target.code && card.dataset.eventStart === target.startDate;
    });

    if (!eventCard) return false;

    try {
      eventCard.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    } catch {
      try {
        eventCard.scrollIntoView(false);
      } catch {
        const listRect = listEl.getBoundingClientRect();
        const cardRect = eventCard.getBoundingClientRect();
        listEl.scrollTop += cardRect.top - listRect.top - 8;
      }
    }

    return true;
  };

  const jumpToNowEvent = () => {
    if (!nextUpcomingEvent?.StartDate) return;

    const target = {
      code: nextUpcomingEvent.Code || '',
      startDate: nextUpcomingEvent.StartDate
    };

    setPendingScrollTarget(target);

    const targetDayKey = getDayKey(new Date(nextUpcomingEvent.StartDate));
    if (selectedDay !== targetDayKey) {
      setSelectedDay(targetDayKey);
      return;
    }

    window.requestAnimationFrame(() => {
      const scrolled = scrollToEvent(target);
      if (scrolled) {
        setPendingScrollTarget(null);
      }
    });
  };

  useEffect(() => {
    if (medalOnly && medalEventCount === 0) {
      setMedalOnly(false);
    }
  }, [medalOnly, medalEventCount]);

  useEffect(() => {
    if (!pendingScrollTarget) return;

    const rafId = window.requestAnimationFrame(() => {
      const scrolled = scrollToEvent(pendingScrollTarget);
      if (scrolled) {
        setPendingScrollTarget(null);
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [pendingScrollTarget, filteredEvents, selectedDay]);

  useEffect(() => {
    const listEl = eventsListRef.current;
    if (!listEl) return undefined;

    const updateScrollState = () => {
      const maxScrollTop = listEl.scrollHeight - listEl.clientHeight;
      if (maxScrollTop <= 1) {
        setHasOverflow(false);
        setCanScrollUp(false);
        setCanScrollDown(false);
        return;
      }

      setHasOverflow(true);
      setCanScrollUp(listEl.scrollTop > 1);
      setCanScrollDown(listEl.scrollTop < maxScrollTop - 1);
    };

    const rafId = window.requestAnimationFrame(updateScrollState);
    const settleTimeoutId = window.setTimeout(updateScrollState, 160);
    let resizeObserver;

    if (typeof window.ResizeObserver !== 'undefined') {
      resizeObserver = new window.ResizeObserver(updateScrollState);
      resizeObserver.observe(listEl);
    }

    const stopTouchPropagation = (event) => {
      event.stopPropagation();
    };

    listEl.addEventListener('scroll', updateScrollState, { passive: true });
    listEl.addEventListener('touchstart', stopTouchPropagation, { passive: true });
    listEl.addEventListener('touchmove', stopTouchPropagation, { passive: true });
    window.addEventListener('resize', updateScrollState);
    window.addEventListener('orientationchange', updateScrollState);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimeoutId);
      listEl.removeEventListener('scroll', updateScrollState);
      listEl.removeEventListener('touchstart', stopTouchPropagation);
      listEl.removeEventListener('touchmove', stopTouchPropagation);
      window.removeEventListener('resize', updateScrollState);
      window.removeEventListener('orientationchange', updateScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [filteredEvents.length, selectedDay, selectedDiscipline, medalOnly, isActive]);

  const nudgeEventsList = (direction) => {
    const listEl = eventsListRef.current;
    if (!listEl) return;

    const step = Math.max(140, Math.round(listEl.clientHeight * 0.55));
    listEl.scrollBy({
      top: direction * step,
      behavior: 'smooth'
    });
  };

  const hasScrollableEvents = hasOverflow;

  const formatDateTime = (dateStr) => {
    if (!hasValidDate(dateStr)) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeOnly = (dateStr) => {
    if (!hasValidDate(dateStr)) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEventTimeRange = (event) => {
    if (event.HideStartDate && event.HideEndDate) {
      return 'Time TBA';
    }

    if (event.HideStartDate) {
      const hiddenStartEndLabel = formatTimeOnly(event.EndDate);
      return hiddenStartEndLabel ? `Ends ${hiddenStartEndLabel}` : 'Start time TBA';
    }

    const startLabel = formatDateTime(event.StartDate);
    if (event.HideEndDate || !hasValidDate(event.EndDate)) return startLabel;

    const endLabel = formatTimeOnly(event.EndDate);
    if (!endLabel) return startLabel;

    return `${startLabel} - ${endLabel}`;
  };

  const getActualTimingNote = (event) => {
    if (!hasValidDate(event.StartDate) || !hasValidDate(event.ActualStartDate)) {
      return '';
    }

    const scheduledMs = new Date(event.StartDate).getTime();
    const actualMs = new Date(event.ActualStartDate).getTime();
    const diffMs = actualMs - scheduledMs;

    if (Math.abs(diffMs) < 60000) {
      return '';
    }

    const timingLabel = diffMs > 0 ? 'Delayed start' : 'Started early';
    const actualTime = formatTimeOnly(event.ActualStartDate);
    return actualTime ? `${timingLabel}: ${actualTime}` : '';
  };

  const getStatusMeta = (statusCode) => {
    const normalized = (statusCode || '').toUpperCase();

    if (normalized === 'RUNNING') {
      return { label: 'Live', variant: 'live' };
    }

    if (normalized === 'FINISHED') {
      return { label: 'Finished', variant: 'finished' };
    }

    if (normalized === 'GETTING_READY') {
      return { label: 'Getting ready', variant: 'ready' };
    }

    if (normalized === 'RESCHEDULED') {
      return { label: 'Rescheduled', variant: 'rescheduled' };
    }

    return { label: 'Scheduled', variant: 'scheduled' };
  };

  const getFlagIconSrc = (countryCode) => {
    if (!countryCode) return DEFAULT_FLAG_ICON;
    return FLAG_ICONS_BY_CODE[countryCode.toUpperCase()] || DEFAULT_FLAG_ICON;
  };

  const getCompetitorCountryLabel = (competitor) => {
    return (competitor?.organisationCode || '').trim().toUpperCase() || 'UNK';
  };

  const getCompetitorCountryName = (competitor) => {
    const code = getCompetitorCountryLabel(competitor);
    return getCountryName(code) || code;
  };

  const getCompetitorNameLabel = (competitor) => {
    const countryLabel = getCompetitorCountryLabel(competitor);
    const name = (competitor?.name || '').trim();

    if (!name) return '';
    if (name.toLowerCase() === countryLabel.toLowerCase()) return '';
    return name;
  };

  const getPreferredIndividualName = (person = {}) => {
    return person.TVInitialName
      || person.PrintInitialName
      || person.TVName
      || person.PrintName
      || [person.GivenName, person.FamilyName].filter(Boolean).join(' ')
      || '';
  };

  const extractCompetitorNames = (competitor, fallbackLabel) => {
    if (!competitor) {
      return [{ name: fallbackLabel || 'Competitor' }];
    }

    const athletes = competitor.Composition?.Athlete || [];

    if (athletes.length > 0) {
      return athletes.map((athlete) => ({
        name: athlete.Description?.TeamName
          || athlete.Description?.TVTeamName
          || getPreferredIndividualName(athlete.Description || {})
          || [athlete.Description?.GivenName, athlete.Description?.FamilyName].filter(Boolean).join(' ')
          || competitor.Description?.TeamName
          || competitor.Description?.TVTeamName
          || competitor.OrganisationCode
          || fallbackLabel
          || 'Athlete',
        organisationCode: athlete.Description?.OrganisationCode || competitor.OrganisationCode || ''
      }));
    }

    return [{
      name: competitor.Description?.TeamName
        || competitor.Description?.TVTeamName
        || competitor.OrganisationCode
        || fallbackLabel
        || 'Competitor',
      organisationCode: competitor.OrganisationCode || ''
    }];
  };

  const dedupeByNameAndCode = (entries) => {
    const seen = new Set();

    return entries.filter((entry) => {
      const normalizedName = (entry.name || '').trim().toLowerCase();
      const normalizedCode = (entry.organisationCode || '').trim().toUpperCase();
      const key = `${normalizedName}::${normalizedCode}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const isGenericCompetitorLabel = (entry) => {
    if (!entry) return true;

    const name = (entry.name || '').trim().toLowerCase();
    const organisationCode = (entry.organisationCode || '').trim().toUpperCase();
    const countryName = (getCountryName(organisationCode) || '').trim().toLowerCase();

    if (!name) return true;
    if (organisationCode && name === organisationCode.toLowerCase()) return true;
    if (countryName && name === countryName) return true;

    return false;
  };

  const mergeCompetitorSources = (primaryEntries, fallbackEntries) => {
    if (!primaryEntries.length) {
      return dedupeByNameAndCode(fallbackEntries);
    }

    const fallbackByCode = fallbackEntries.reduce((acc, entry) => {
      const code = (entry.organisationCode || '').trim().toUpperCase();
      if (!code) return acc;
      if (!acc[code]) acc[code] = [];
      acc[code].push(entry);
      return acc;
    }, {});

    const primaryByCode = primaryEntries.reduce((acc, entry) => {
      const code = (entry.organisationCode || '').trim().toUpperCase();
      if (!code) return acc;
      if (!acc[code]) acc[code] = [];
      acc[code].push(entry);
      return acc;
    }, {});

    const merged = [];

    Object.entries(primaryByCode).forEach(([code, entries]) => {
      const fallbackForCode = fallbackByCode[code] || [];
      const hasGenericPrimary = entries.some(isGenericCompetitorLabel);
      const hasNamedFallback = fallbackForCode.some((entry) => !isGenericCompetitorLabel(entry));

      if (hasGenericPrimary && hasNamedFallback) {
        merged.push(...fallbackForCode);
        return;
      }

      merged.push(...entries);
    });

    return dedupeByNameAndCode(merged);
  };

  const extractStartListCompetitors = (event) => {
    const starts = event.StartList?.Start || [];

    return starts
      .filter((start) => start.Competitor)
      .flatMap((start) => {
        const competitor = start.Competitor;
        const fallbackLabel = competitor.OrganisationCode || 'Competitor';

        return extractCompetitorNames(competitor, fallbackLabel).map((entry) => ({
          name: entry.name,
          organisationCode: entry.organisationCode || competitor.OrganisationCode || '',
          source: 'startlist'
        }));
      });
  };

  const extractOrganisationFallbackCompetitors = (event) => {
    const orgs = event.Organisations || [];

    return orgs
      .filter((org) => org?.Code)
      .flatMap((org) => {
        const organisationCode = org.Code;
        const countryLabel = getCountryName(organisationCode) || organisationCode;
        const orgCompetitors = org.Competitors || [];

        if (orgCompetitors.length === 0) {
          return [{
            name: countryLabel,
            organisationCode,
            source: 'organisations'
          }];
        }

        return orgCompetitors.map((entry) => {
          const competitor = entry?.Competitor || {};
          const name = competitor.TeamName
            || competitor.TVTeamName
            || getPreferredIndividualName(competitor)
            || [competitor.GivenName, competitor.FamilyName].filter(Boolean).join(' ')
            || countryLabel;

          return {
            name,
            organisationCode,
            source: 'organisations'
          };
        });
      });
  };

  const getMatchupCompetitors = (event) => {
    const startListCompetitors = dedupeByNameAndCode(extractStartListCompetitors(event));
    const organisationFallbackCompetitors = dedupeByNameAndCode(extractOrganisationFallbackCompetitors(event));

    const startListAussies = startListCompetitors.filter((competitor) => competitor.organisationCode === 'AUS');
    const startListOpposition = startListCompetitors.filter((competitor) => {
      const organisationCode = competitor.organisationCode;
      return organisationCode && organisationCode !== 'AUS';
    });

    const fallbackAussies = organisationFallbackCompetitors.filter((competitor) => competitor.organisationCode === 'AUS');
    const fallbackOpposition = organisationFallbackCompetitors.filter((competitor) => {
      const organisationCode = competitor.organisationCode;
      return organisationCode && organisationCode !== 'AUS';
    });

    let aussieCompetitors = mergeCompetitorSources(startListAussies, fallbackAussies);
    let oppositionCompetitors = mergeCompetitorSources(startListOpposition, fallbackOpposition);

    if (aussieCompetitors.length === 0) {
      aussieCompetitors = fallbackAussies;
    }

    if (oppositionCompetitors.length === 0) {
      oppositionCompetitors = fallbackOpposition;
    }

    return {
      aussieCompetitors: dedupeByNameAndCode(aussieCompetitors),
      oppositionCompetitors: dedupeByNameAndCode(oppositionCompetitors),
      hasStartList: (event.StartList?.Start || []).length > 0
    };
  };

  const getMatchupSummary = (event) => {
    const { aussieCompetitors, oppositionCompetitors, hasStartList } = getMatchupCompetitors(event);

    return {
      aussieCompetitors,
      oppositionCompetitors,
      hasStartList,
      aussieCount: aussieCompetitors.length,
      oppositionCount: oppositionCompetitors.length
    };
  };

  const hasMeaningfulResultValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  };

  const hasResultDetail = (result = {}) => {
    return hasMeaningfulResultValue(result.Result)
      || hasMeaningfulResultValue(result.WLT)
      || hasMeaningfulResultValue(result.IRM)
      || hasMeaningfulResultValue(result.ResultCode)
      || hasMeaningfulResultValue(result.ResultCodeDescription);
  };

  const isOfficialOrFinished = (event) => {
    const scheduleStatus = (event.ScheduleStatus || '').toUpperCase();
    const resultStatus = (event.ResultStatus || '').toUpperCase();
    const odfResultStatus = (event.ODFResultStatus || '').toUpperCase();

    return scheduleStatus === 'FINISHED'
      || resultStatus === 'OFFICIAL'
      || resultStatus === 'UNOFFICIAL'
      || odfResultStatus === 'OFFICIAL'
      || odfResultStatus === 'UNOFFICIAL';
  };

  const normalizeStartResultRows = (event) => {
    const starts = event.StartList?.Start || [];

    return starts
      .filter((start) => start?.Competitor)
      .map((start) => {
        const competitor = start.Competitor;
        const result = start.Result || {};

        return {
          competitorCode: competitor.Code || '',
          organisationCode: (competitor.OrganisationCode || '').toUpperCase(),
          resultType: result.Type || '',
          resultValue: result.Result,
          wlt: result.WLT || '',
          irm: result.IRM || '',
          resultCode: result.ResultCode || '',
          resultCodeDescription: result.ResultCodeDescription || '',
          hasDetail: hasResultDetail(result)
        };
      });
  };

  const parseNumericScore = (value) => {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getWinnerDisplayLabel = (event, winnerCode) => {
    if (!winnerCode) return '';

    const starts = event.StartList?.Start || [];
    const winnerStart = starts.find((start) => start?.Competitor?.Code === winnerCode);
    const winnerCompetitor = winnerStart?.Competitor;

    if (winnerCompetitor) {
      const winnerNames = extractCompetitorNames(winnerCompetitor, winnerCompetitor.OrganisationCode || 'Winner');
      const primaryName = (winnerNames[0]?.name || '').trim();
      const winnerCodeLabel = (winnerCompetitor.OrganisationCode || '').trim().toUpperCase();
      const winnerCountry = getCountryName(winnerCodeLabel) || winnerCodeLabel;

      if (!primaryName) return winnerCountry || '';
      if (winnerCodeLabel && primaryName.toUpperCase() === winnerCodeLabel) return winnerCountry || primaryName;
      if (winnerCountry && primaryName.toLowerCase() === winnerCountry.toLowerCase()) return winnerCountry;
      return primaryName;
    }

    const winnerOrg = (event.Organisations || []).find((org) => {
      const competitors = org?.Competitors || [];
      return competitors.some((entry) => entry?.Competitor?.Code === winnerCode);
    });

    if (winnerOrg?.Code) {
      return getCountryName(winnerOrg.Code) || winnerOrg.Code;
    }

    return '';
  };

  const getWinnerState = (event, ausRows) => {
    const winnerCode = event.Winner?.WinnerCode || '';
    if (winnerCode) {
      const isAusWinner = ausRows.some((row) => row.competitorCode && row.competitorCode === winnerCode);
      return isAusWinner ? 'won' : 'lost';
    }

    const ausWlt = (ausRows.find((row) => row.wlt)?.wlt || '').toUpperCase();
    if (ausWlt === 'W') return 'won';
    if (ausWlt === 'L') return 'lost';
    if (ausWlt === 'T') return 'draw';

    return '';
  };

  const getResultWinnerMeta = (event, ausRows, oppositionRows) => {
    const winnerCode = event.Winner?.WinnerCode || '';
    const winnerState = getWinnerState(event, ausRows);

    if (!winnerState) {
      return { winnerState: '', winnerLabel: '' };
    }

    if (winnerState === 'draw') {
      return { winnerState, winnerLabel: '' };
    }

    let winnerLabel = getWinnerDisplayLabel(event, winnerCode);

    if (!winnerLabel && winnerState === 'won') {
      winnerLabel = 'Australia';
    }

    if (!winnerLabel && winnerState === 'lost') {
      const oppositionCode = oppositionRows.find((row) => row.organisationCode)?.organisationCode || '';
      winnerLabel = getCountryName(oppositionCode) || oppositionCode || 'Opposition';
    }

    return { winnerState, winnerLabel };
  };

  const formatResultSummary = (event) => {
    if (!isOfficialOrFinished(event)) return null;

    const rows = normalizeStartResultRows(event);
    const ausRows = rows.filter((row) => row.organisationCode === 'AUS');
    const oppositionRows = rows.filter((row) => row.organisationCode && row.organisationCode !== 'AUS');

    const ausRow = ausRows.find((row) => row.hasDetail) || ausRows[0];
    const oppositionRow = oppositionRows.find((row) => row.hasDetail) || oppositionRows[0];

    const { winnerState, winnerLabel } = getResultWinnerMeta(event, ausRows, oppositionRows);

    if (ausRow) {
      const ausScore = parseNumericScore(ausRow.resultValue);
      const oppositionScore = oppositionRow ? parseNumericScore(oppositionRow.resultValue) : null;

      if (ausScore !== null && oppositionScore !== null) {
        return {
          summary: `${ausScore} - ${oppositionScore}`,
          resultType: ausRow.resultType || oppositionRow?.resultType || '',
          winnerState,
          winnerLabel
        };
      }

      if (hasMeaningfulResultValue(ausRow.resultValue)) {
        return {
          summary: String(ausRow.resultValue),
          resultType: ausRow.resultType || '',
          winnerState,
          winnerLabel
        };
      }

      if (ausRow.resultCodeDescription) {
        return {
          summary: ausRow.resultCodeDescription,
          resultType: ausRow.resultType || '',
          winnerState,
          winnerLabel
        };
      }

      if (ausRow.resultCode) {
        return {
          summary: ausRow.resultCode,
          resultType: ausRow.resultType || '',
          winnerState,
          winnerLabel
        };
      }

      if (ausRow.irm) {
        return {
          summary: ausRow.irm,
          resultType: ausRow.resultType || 'Status',
          winnerState,
          winnerLabel
        };
      }
    }

    if (winnerState) {
      return {
        summary: 'Official result recorded',
        resultType: '',
        winnerState,
        winnerLabel
      };
    }

    return null;
  };

  if (error) {
    return (
      <div className="tab-content error-state">
        <p className="error-message">Failed to load Aussie schedule</p>
        <button className="retry-button" onClick={refetch}>Retry</button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="tab-content loading-state">
        <SkeletonLoader count={5} type="card" />
      </div>
    );
  }

  return (
    <div className="tab-content aussies-schedule">
      {dayOptions.length > 0 && (
        <div className="schedule-filter">
          <div className="schedule-filter__group">
            <label htmlFor="aussies-day-select" className="schedule-filter__label">Day</label>
            <div className="schedule-filter__day-controls">
              <div className="schedule-filter__quick-buttons" role="group" aria-label="Quick day filters">
                <button
                  type="button"
                  className={`schedule-filter__quick-button ${selectedDay === 'all' ? 'schedule-filter__quick-button--active' : ''}`}
                  onClick={() => setSelectedDay('all')}
                  aria-pressed={selectedDay === 'all'}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`schedule-filter__quick-button ${selectedDay === todayDayKey ? 'schedule-filter__quick-button--active' : ''}`}
                  onClick={() => {
                    if (hasTodayEvents) setSelectedDay(todayDayKey);
                  }}
                  aria-pressed={selectedDay === todayDayKey}
                  disabled={!hasTodayEvents}
                  title={hasTodayEvents ? 'Jump to today' : 'No events scheduled for today'}
                >
                  Today
                </button>
              </div>
              <select
                id="aussies-day-select"
                className="schedule-filter__select"
                value={selectedDay}
                onChange={(event) => setSelectedDay(event.target.value)}
              >
                <option value="all">All Days ({aussieEvents.length})</option>
                {dayOptions.map((option) => (
                  <option key={option.key} value={option.key}>{option.label} ({(eventsByDay[option.key] || []).length})</option>
                ))}
              </select>
              <button
                type="button"
                className="schedule-filter__quick-button schedule-filter__quick-button--compact"
                onClick={jumpToNowEvent}
                disabled={!hasNextUpcomingEvent}
                title={hasNextUpcomingEvent ? 'Jump to next upcoming event' : 'No upcoming events for selected filters'}
                aria-label="Jump to next upcoming event"
              >
                Up Next
              </button>
            </div>
          </div>

          <div className="schedule-filter__group">
            <label htmlFor="aussies-discipline-select" className="schedule-filter__label">Sport</label>
            <select
              id="aussies-discipline-select"
              className="schedule-filter__select"
              value={selectedDiscipline}
              onChange={(event) => setSelectedDiscipline(event.target.value)}
            >
              <option value="all">All ({allDisciplineCount})</option>
              {disciplineOptions.map((disciplineCode) => {
                const disciplineName = getDisciplineName(disciplineCode);
                const disciplineCount = disciplineStats[disciplineCode]?.current || 0;
                return (
                  <option key={disciplineCode} value={disciplineCode}>
                    {disciplineName} ({disciplineCount})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="schedule-filter__toggle-row">
            <div className="schedule-filter__toggle-copy">
              <span className="schedule-filter__label">Medal events only</span>
            </div>
            <button
              type="button"
              className={`schedule-filter__switch ${medalOnly ? 'schedule-filter__switch--active' : ''}`}
              onClick={() => setMedalOnly((prev) => !prev)}
              aria-pressed={medalOnly}
              aria-label={`Medal events only ${medalOnly ? 'on' : 'off'}`}
              title={medalOnly ? 'Medal events only is on' : 'Medal events only is off'}
              disabled={medalEventCount === 0}
            >
              <span className="schedule-filter__switch-track">
                <span className="schedule-filter__switch-thumb"></span>
              </span>
              <span className="schedule-filter__switch-text">{medalOnly ? 'On' : 'Off'}</span>
            </button>
          </div>

          <div className="schedule-filter__summary" aria-live="polite">
            <span>
              {filteredEvents.length} events shown · {selectedDaySummary} · {selectedDisciplineSummary} · {medalOnly ? 'Medal only' : 'All events'}
            </span>
            <button
              type="button"
              className={`schedule-filter__clear ${hasActiveFilters ? '' : 'schedule-filter__clear--hidden'}`.trim()}
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              aria-hidden={!hasActiveFilters}
              tabIndex={hasActiveFilters ? 0 : -1}
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      <div className="events-list" ref={eventsListRef}>
        {dayOptions.length === 0 ? (
          <div className="no-events">No Australian competitors scheduled</div>
        ) : filteredEvents.length === 0 ? (
          <div className="no-events">
            <p>No events match the selected filters</p>
            <div className="no-events__actions">
              {selectedDiscipline !== 'all' && (
                <button type="button" className="no-events__action" onClick={() => setSelectedDiscipline('all')}>
                  Clear discipline
                </button>
              )}
              {medalOnly && (
                <button type="button" className="no-events__action" onClick={() => setMedalOnly(false)}>
                  Show all events
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredEvents.map((event, idx) => {
            const statusMeta = getStatusMeta(event.ScheduleStatus);
            const actualTimingNote = getActualTimingNote(event);
            const disciplineName = getDisciplineName(event.DisciplineCode) || event.DisciplineCode || 'Discipline';
            const eventTitle = event.EventDescription || 'Event';
            const { aussieCompetitors, oppositionCompetitors, hasStartList, aussieCount, oppositionCount } = getMatchupSummary(event);
            const hasMatchup = aussieCount > 0 || oppositionCount > 0;
            const resultSummary = formatResultSummary(event);

            return (
              <div
                key={`${event.Code}-${idx}`}
                className="event-card aussie-card"
                data-event-code={event.Code || ''}
                data-event-start={event.StartDate || ''}
              >
                <div className="event-badges">
                  <div className={`event-status event-status--${statusMeta.variant}`}>
                    {statusMeta.label}
                  </div>
                  {event.Medal && (
                    <div className="event-medal" aria-label="Medal Event" title="Medal Event">
                      <span className="medal-disc medal-disc--gold"></span>
                      <span className="event-medal__label">Medal Event</span>
                    </div>
                  )}
                </div>
                <div className="event-header">
                  <div className="event-discipline">
                    <span className="event-discipline__name">{disciplineName}</span>
                  </div>
                  <div className="event-title">
                    {eventTitle}
                  </div>
                  <div className="event-phase">
                    {event.PhasePrintDescription || event.PhaseDescription || event.PhaseCode || ''}
                  </div>
                </div>
                <div className="event-details">
                  <div className="detail">
                    <span className="label">Time:</span>
                    <span className="value">{formatEventTimeRange(event)}</span>
                  </div>
                  {actualTimingNote && (
                    <div className="detail detail--subtle">
                      <span className="label">Update:</span>
                      <span className="value">{actualTimingNote}</span>
                    </div>
                  )}
                  {resultSummary && (
                    <div className="detail detail--subtle detail--result">
                      <span className="label">Result:</span>
                      <span className="value">
                        {resultSummary.resultType ? `${resultSummary.resultType}: ` : ''}{resultSummary.summary}
                      </span>
                      {resultSummary.winnerLabel && resultSummary.winnerState !== 'draw' && (
                        <span className="result-winner-text">Winner: {resultSummary.winnerLabel}</span>
                      )}
                      {resultSummary.winnerState && (
                        <span className={`result-pill result-pill--${resultSummary.winnerState}`}>
                          {resultSummary.winnerState === 'won' ? 'AUS won' : resultSummary.winnerState === 'lost' ? 'AUS lost' : 'Draw'}
                        </span>
                      )}
                    </div>
                  )}
                  {event.UnitDescription && (
                    <div className="detail">
                      <span className="label">Session:</span>
                      <span className="value">{event.UnitDescription}</span>
                    </div>
                  )}
                  {showLocation && (event.LocationDescription || event.VenueDescription) && (
                    <div className="detail">
                      <span className="label">Location:</span>
                      <span className="value">{event.LocationDescription || event.VenueDescription}</span>
                    </div>
                  )}
                </div>
                {hasMatchup && (
                  <div className="aussie-matchup">
                    <div className="aussie-competitors">
                      <div className="aussie-competitors__header">
                        <span className="label">Competitors</span>
                        <span className="aussie-competitors__count">{aussieCount}</span>
                      </div>
                      <div className="names">
                        {aussieCompetitors.map((comp, i) => (
                          <div key={i} className="name">
                            <img className="country-flag" src={ausFlagIcon} alt="" loading="lazy" decoding="async" />
                            <span
                              className="competitor-code"
                              title={getCompetitorCountryName({ ...comp, organisationCode: comp.organisationCode || 'AUS' })}
                              aria-label={getCompetitorCountryName({ ...comp, organisationCode: comp.organisationCode || 'AUS' })}
                            >
                              {getCompetitorCountryLabel({ ...comp, organisationCode: comp.organisationCode || 'AUS' })}:
                            </span>
                            <span className="competitor-name">{getCompetitorNameLabel({ ...comp, organisationCode: comp.organisationCode || 'AUS' }) || 'Athlete'}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="aussie-competitors aussie-competitors--opposition">
                      <div className="aussie-competitors__header">
                        <span className="label">Opposition</span>
                        <span className="aussie-competitors__count">{oppositionCount}</span>
                      </div>
                      {oppositionCount > 0 ? (
                        <div className="names">
                          {oppositionCompetitors.map((comp, i) => (
                            <div key={`${comp.organisationCode}-${comp.name}-${i}`} className="name">
                              <img
                                className="country-flag"
                                src={getFlagIconSrc(comp.organisationCode)}
                                alt=""
                                loading="lazy"
                                decoding="async"
                              />
                              <span
                                className="competitor-code"
                                title={getCompetitorCountryName(comp)}
                                aria-label={getCompetitorCountryName(comp)}
                              >
                                {getCompetitorCountryLabel(comp)}:
                              </span>
                              <span className="competitor-name">{getCompetitorNameLabel(comp)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="aussie-competitors__tbc">{hasStartList ? 'Opponent not listed' : 'Opponent TBC'}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {
        <div className={`schedule-scroll-bar schedule-scroll-bar--controls-only ${canScrollDown ? 'schedule-scroll-bar--has-below' : ''}`.trim()}>
          {hasScrollableEvents && (
            <div className="schedule-scroll-bar__controls">
              <button
                type="button"
                className="schedule-scroll-bar__btn"
                onClick={() => nudgeEventsList(-1)}
                disabled={!canScrollUp}
                aria-label="Scroll aussies event list up"
              >
                <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M8 3L14 11H2Z" />
                </svg>
              </button>
              <button
                type="button"
                className="schedule-scroll-bar__btn"
                onClick={() => nudgeEventsList(1)}
                disabled={!canScrollDown}
                aria-label="Scroll aussies event list down"
              >
                <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M8 13L2 5h12Z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      }
    </div>
  );
};
